import React, { useMemo } from 'react';
import { ArrowRight, CalendarClock, Flag, Gauge, Mic, PlayCircle, Sparkles } from 'lucide-react';
import { InlineArea, InlineText } from './inline';
import { ClientCard, TeamCard } from './TeamCard';
import { Card, daysBetween, Empty, euros, hours, Label, longDate, producedHint, shortDate, toProduceHint } from './ui';
import { elapsed, periodState, rangeLabel, toDay } from './roadmap/dates';
import { iterationColors, sprintShort } from './roadmap/colors';
import ProgressCurve from './roadmap/ProgressCurve';
import FrameCard from './FrameCard';

// Onglet Aperçu : l'essentiel d'un coup d'œil. À gauche le projet (ce qu'il vise, où il en est selon Nexus, ce qui
// est en cours, les jalons) ; à droite les gens (équipe, client), les réunions et les chiffres.

const LinkButton = ({ children, onClick }) => (
  <button type="button" onClick={onClick} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-indigo-700">
    {children} <ArrowRight size={12} />
  </button>
);

const when = (iso, today) => {
  const days = daysBetween(today, iso);
  if (days === 0) return "aujourd'hui";
  if (days === 1) return 'demain';
  if (days === -1) return 'hier';
  if (days > 1 && days < 7) return `dans ${days} j`;
  return shortDate(iso);
};

const Pitch = ({ project, actions }) => (
  <Card>
    <Label className="mb-1">Le projet</Label>
    <InlineArea value={project.brief} placeholder="En deux phrases : ce que le projet doit apporter au client"
      onSave={(brief) => actions.patchProject({ brief }).catch(() => {})} className="text-[15px] text-slate-800" rows={1} />
    <div className="mt-2 flex items-baseline gap-2 text-sm">
      <span className="shrink-0 text-xs font-medium text-slate-400">Vision</span>
      <InlineText value={project.vision} placeholder="La vision du projet, en une ligne" ariaLabel="Vision du projet"
        onSave={(vision) => actions.patchProject({ vision }).catch(() => {})} className="w-full text-sm text-slate-600" />
    </div>
  </Card>
);

const NexusGlance = ({ overview, today, openTab }) => {
  const glance = overview?.glance;
  return (
    <section className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-white px-4 py-4 shadow-sm sm:px-5">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Sparkles size={16} className="text-indigo-500" /> Où on en est</h2>
        <LinkButton onClick={() => openTab('carnet')}>Carnet de Nexus</LinkButton>
      </div>
      {!overview ? (
        <div className="h-12 animate-pulse rounded-lg bg-indigo-50" />
      ) : glance?.progress ? (
        <p className="whitespace-pre-line text-[15px] leading-relaxed text-slate-800">{glance.progress}</p>
      ) : (
        <p className="text-sm text-slate-500">Nexus n'a pas encore fait le point sur ce projet : il le fera à sa prochaine relecture du carnet.</p>
      )}
      {glance?.news?.length > 0 && (
        <div className="mt-3">
          <Label className="mb-1">Quoi de neuf</Label>
          <ul className="space-y-1">
            {glance.news.slice(0, 4).map((item) => (
              <li key={`${item.date}${item.text}`} className="flex items-baseline gap-2 text-[13px] leading-snug">
                <span className="w-16 shrink-0 text-[11px] tabular-nums text-slate-400">{when(item.date, today)}</span>
                <span className="min-w-0 flex-1 text-slate-700">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

const NowCard = ({ iterations, today, openIteration, openTab, steering = null }) => {
  const day = toDay(today);
  const colors = useMemo(() => iterationColors(iterations), [iterations]);
  const current = iterations.filter((it) => periodState(toDay(it.start_date), toDay(it.end_date), day) === 'current');
  const next = iterations.filter((it) => periodState(toDay(it.start_date), toDay(it.end_date), day) === 'upcoming')
    .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)))[0];
  return (
    <Card title="En cours" icon={PlayCircle} action={<LinkButton onClick={() => openTab('roadmap')}>Roadmap</LinkButton>}>
      {current.length ? (
        <ul className="space-y-3">
          {current.map((iteration) => {
            const color = colors.get(iteration.id);
            const start = toDay(iteration.start_date);
            const end = toDay(iteration.end_date);
            const sprintIndex = iteration.sprints.findIndex((s) => periodState(toDay(s.start_date), toDay(s.end_date), day) === 'current');
            const sprint = iteration.sprints[sprintIndex];
            const total = Number(iteration.total_tokens) || 0;
            const steered = steering?.current?.find((row) => Number(row.id) === Number(iteration.id));
            return (
              <li key={iteration.id}>
                <button type="button" onClick={() => openIteration(iteration.id, sprint?.id)} className="block w-full rounded-xl p-2 -m-2 text-left hover:bg-slate-50">
                  <div className="flex items-baseline gap-2">
                    <span className={`h-2 w-2 shrink-0 translate-y-[-1px] rounded-full ${color.dot}`} />
                    <span className="truncate text-sm font-semibold text-slate-800">{iteration.name}</span>
                    <span className="ml-auto shrink-0 text-[11px] text-slate-400">{rangeLabel(start, end)} · encore {end - day} j</span>
                  </div>
                  {iteration.description && <p className="mt-0.5 line-clamp-2 pl-4 text-[13px] leading-snug text-slate-600">{iteration.description}</p>}
                  <div className="mt-2 flex items-center gap-3 pl-4">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100" title="Temps écoulé">
                      <div className="h-full rounded-full bg-slate-300" style={{ width: `${elapsed(start, end, day) * 100}%` }} />
                    </div>
                    <span className="shrink-0 text-[11px] tabular-nums text-slate-500">
                      {total ? `${iteration.completed_tokens}/${total} pts` : 'sans objectifs chiffrés'}
                      {steered?.progress ? ` · réalisée à ${Math.round(steered.progress.percent)} %` : ''}
                    </span>
                  </div>
                  {sprint && (
                    <p className="mt-1.5 pl-4 text-xs text-slate-500">
                      <span className={`mr-1.5 rounded-full px-1.5 py-px text-[10px] font-semibold ${color.soft} ${color.text}`}>Sprint {sprintShort(sprint, sprintIndex)}</span>
                      {sprint.vision || 'vision du sprint à définir'} · jusqu'au {shortDate(sprint.end_date)}
                    </p>
                  )}
                </button>
                {steered?.curve && <div className="pl-4"><ProgressCurve curve={steered.curve} /></div>}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">Aucune itération en cours.</p>
      )}
      {next && (
        <p className="mt-3 border-t border-slate-100 pt-2.5 text-xs text-slate-500">
          Ensuite : <button type="button" onClick={() => openIteration(next.id)} className="font-medium text-slate-700 hover:text-indigo-700">{next.name}</button>
          {' '}dans {toDay(next.start_date) - day} j ({shortDate(next.start_date)})
        </p>
      )}
    </Card>
  );
};

const Milestones = ({ overview, today, openTab }) => {
  const items = overview?.milestones || [];
  return (
    <Card title="Jalons" icon={Flag} action={<LinkButton onClick={() => openTab('roadmap')}>Tous</LinkButton>}>
      {!overview ? <div className="h-10 animate-pulse rounded-lg bg-slate-50" /> : items.length ? (
        <ul className="space-y-1.5">
          {items.map((m) => {
            const done = m.status === 'done';
            return (
              <li key={m.id} className="flex items-baseline gap-2.5 text-[13px] leading-snug">
                <span className={`h-2.5 w-2.5 shrink-0 translate-y-[1px] rotate-45 rounded-[2px] ${done ? 'bg-emerald-500' : m.late ? 'bg-rose-500' : 'bg-rose-300'}`} />
                <span className={`w-20 shrink-0 text-[11px] tabular-nums ${m.late ? 'font-semibold text-rose-600' : 'text-slate-400'}`}>
                  {m.late ? 'en retard' : done ? 'atteint' : when(m.date, today)}
                </span>
                <span className={`min-w-0 flex-1 ${done ? 'text-slate-400' : 'text-slate-700'}`}>{m.name}</span>
                {m.owner_name && <span className="shrink-0 text-[11px] text-slate-400">{m.owner_name}</span>}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">Aucun jalon posé.</p>
      )}
    </Card>
  );
};

const Meetings = ({ overview, today, openTab, navigate }) => {
  const meetings = overview?.meetings;
  return (
    <Card title="Réunions" icon={Mic} action={<LinkButton onClick={() => openTab('reunions')}>Ouvrir</LinkButton>}>
      {!overview ? <div className="h-10 animate-pulse rounded-lg bg-slate-50" /> : (
        <div className="space-y-3">
          {meetings?.upcoming?.length > 0 ? (
            <ul className="space-y-1.5">
              {meetings.upcoming.map((m) => (
                <li key={m.event_id} className="flex items-baseline gap-2 text-[13px]">
                  <CalendarClock size={12} className="shrink-0 translate-y-[1px] text-slate-400" />
                  <span className="w-24 shrink-0 text-[11px] text-slate-500">{when(m.date, today)}{m.time ? ` · ${m.time}` : ''}</span>
                  <span className="min-w-0 flex-1 truncate text-slate-700" title={m.external?.join(', ')}>{m.title}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">{meetings?.connected === false ? 'Agenda Google non relié.' : 'Rien de prévu dans ton agenda pour ce projet.'}</p>
          )}
          {meetings?.tactical && (
            <div>
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <Label>En tactique · {longDate(meetings.tactical.date)}</Label>
                {meetings.tactical.closed && (
                  <button type="button" onClick={() => navigate(`/meetings/tactique/${meetings.tactical.meeting_id}`)}
                    className="text-[11px] font-medium text-slate-400 hover:text-indigo-700">
                    Toute la réunion
                  </button>
                )}
              </div>
              <ul className="space-y-1">
                {meetings.tactical.news.slice(0, 4).map((item) => (
                  <li key={item} className="flex gap-2 text-[13px] leading-snug text-slate-700">
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-indigo-400" />
                    <span className="min-w-0 flex-1">{item}</span>
                  </li>
                ))}
              </ul>
              {meetings.tactical.notes && (
                <p className="mt-1 whitespace-pre-wrap text-xs text-slate-500">{meetings.tactical.notes}</p>
              )}
            </div>
          )}
          {meetings?.recorded?.length > 0 && (
            <div>
              <Label className="mb-1">Dernières enregistrées</Label>
              <ul className="space-y-1">
                {meetings.recorded.map((m) => (
                  <li key={m.id}>
                    <button type="button" onClick={() => navigate(`/meetings/${m.id}`)} className="flex w-full items-baseline gap-2 text-left text-[13px] hover:text-indigo-700">
                      <span className="w-24 shrink-0 text-[11px] text-slate-400">{m.started_at ? longDate(m.started_at) : ''}</span>
                      <span className="min-w-0 flex-1 truncate text-slate-700">{m.title || 'Réunion'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {overview.open_points > 0 && (
            <button type="button" onClick={() => openTab('reunions')}
              className="w-full rounded-lg bg-indigo-50 px-2.5 py-1.5 text-left text-xs font-medium text-indigo-700 hover:bg-indigo-100">
              {overview.open_points} point{overview.open_points > 1 ? 's' : ''} à aborder avec le client
            </button>
          )}
        </div>
      )}
    </Card>
  );
};

const Figures = ({ project, iterations, steering, openTab }) => {
  const sum = (key) => iterations.reduce((total, it) => total + (Number(it[key]) || 0), 0);
  const time = { used: sum('consumed_time_hours'), planned: sum('budget_time_hours') };
  const purchases = { used: sum('consumed_purchase_eur'), planned: sum('budget_purchase_eur') };
  const ca = steering?.project;
  const tile = (label, value, detail, className = '') => (
    <div className={`min-w-0 rounded-xl bg-slate-50 px-3 py-2 ${className}`}>
      <p className="truncate text-[11px] text-slate-500">{label}</p>
      <p className="truncate text-sm font-semibold tabular-nums text-slate-800">{value}</p>
      {detail && <p className="truncate text-[11px] text-slate-400">{detail}</p>}
    </div>
  );
  return (
    <Card title="Chiffres" icon={Gauge} action={<LinkButton onClick={() => openTab('finances')}>Finances</LinkButton>}>
      <div className="grid grid-cols-2 gap-2">
        {tile('CA signé', euros(project.calculated_turnover), project.invoiced_turnover ? `${euros(project.invoiced_turnover)} facturés` : 'rien de facturé')}
        {tile('Itérations', String(iterations.length), `${iterations.reduce((n, it) => n + it.sprints.length, 0)} sprints`)}
        {tile('CA produit', steering ? euros(ca?.ca_produced_eur) : '—', steering ? producedHint(ca) : null)}
        {tile('CA à produire', steering ? euros(ca?.ca_to_produce_eur) : '—', steering ? toProduceHint(ca) : null)}
        {tile('Heures', hours(time.used), time.planned ? `sur ${hours(time.planned)} prévues` : null)}
        {tile('Achats', euros(purchases.used), purchases.planned ? `sur ${euros(purchases.planned)} prévus` : null)}
      </div>
    </Card>
  );
};

const Overview = ({ data, today, openTab, openIteration, navigate, parentCircle }) => {
  const { project, iterations, overview, focus, circles, employees, clients, actions } = data;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-4">
        <Pitch project={project} actions={actions} />
        <FrameCard frame={data.frame} roadmap={data.domains?.roadmap} onSave={actions.saveFrame} />
        <NexusGlance overview={overview} today={today} openTab={openTab} />
        <NowCard iterations={iterations} today={today} openIteration={openIteration} openTab={openTab} steering={data.steering} />
        <Milestones overview={overview} today={today} openTab={openTab} />
        {overview?.error && <Empty>{overview.error}</Empty>}
      </div>
      <div className="min-w-0 space-y-4">
        <TeamCard project={project} focus={focus} circles={circles} employees={employees} parentCircle={parentCircle}
          overview={overview} actions={actions} />
        <ClientCard project={project} clients={clients} actions={actions} />
        <Meetings overview={overview} today={today} openTab={openTab} navigate={navigate} />
        <Figures project={project} iterations={iterations} steering={data.steering} openTab={openTab} />
      </div>
    </div>
  );
};

export default Overview;
